import { fmtDateTime } from "../lib/format";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import QRCode from "qrcode";
import { api } from "../api/client";
import type { CreatedAssessment, CreateAssessmentInput, MeResponse } from "../api/types";
import { printCandidateSheet, windowText } from "../lib/printSheet";

export default function NewAssessmentPage() {
  const navigate = useNavigate();
  const { data: lookups } = useQuery({ queryKey: ["lookups"], queryFn: api.lookups });

  const [form, setForm] = useState<CreateAssessmentInput>({
    first_name: "",
    last_name: "",
    email: "",
    contact_number: "",
    home: "",
    region: "",
    position: "",
    assessment_date: new Date().toISOString().slice(0, 10),
  });
  const [created, setCreated] = useState<CreatedAssessment | null>(null);
  const [retakeNeeded, setRetakeNeeded] = useState<string | null>(null);
  const { data: me, error: meError, isPending: mePending, refetch: refetchMe } = useQuery({ queryKey: ["me"], queryFn: api.me });

  const createMut = useMutation({
    mutationFn: (body: CreateAssessmentInput) =>
      api.assessments.create({
        ...body,
        email: body.email.trim(),
        contact_number: body.contact_number?.trim() || null,
        home: body.home || null,
        region: body.region || null,
        position: body.position || null,
      }),
    onSuccess: (res) => { setRetakeNeeded(null); setCreated(res); },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.startsWith("409:")) setRetakeNeeded(msg.replace(/^409:\s*/, ""));
      else setRetakeNeeded(null);
    },
  });

  const set = (k: keyof CreateAssessmentInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  if (created)
    return (
      <CreatedScreen
        created={created}
        me={me}
        candidateName={`${form.first_name} ${form.last_name}`.trim()}
        home={form.home || null}
        position={form.position || null}
        onDone={() => navigate(`/assessment/${created.id}`)}
      />
    );

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-liaise-navy">New assessment</h1>
        <p className="text-sm text-slate-500">Set up a sitting and hand the quiz link or QR code to the candidate.</p>
      </div>

      {mePending && <p role="status" className="text-sm text-slate-600">Loading assessment availability…</p>}
      {meError && <div role="alert" className="card p-4 text-sm text-liaise-red">Assessment availability could not be loaded. <button type="button" className="underline" onClick={() => refetchMe()}>Try again</button></div>}
      {me && (
        <div className="card p-4 bg-liaise-navy-50/50 border border-liaise-navy-200 text-sm space-y-2">
          <p className="font-semibold text-liaise-navy">For managers</p>
          <ul className="space-y-1 text-slate-600">
            <li>• <strong>When it can be taken:</strong> {windowText(me.test_window)}</li>
            <li>• <strong>How long the link lasts:</strong> {me.link_days} days from creation. The link expiry does not extend the allowed assessment hours.</li>
            <li>• <strong>Timed:</strong> {me.time_limit_minutes > 0 ? `${me.time_limit_minutes} minutes once the candidate starts, then it submits automatically.` : "No time limit is configured."}</li>
            <li>• Share the link or print the QR code on the next screen. The candidate needs no login.</li>
          </ul>
          {me.manager_note && <p className="text-slate-600 pt-1 border-t border-liaise-navy-200/60 whitespace-pre-wrap">{me.manager_note}</p>}
        </div>
      )}

      <form
        className="card p-6 grid grid-cols-1 md:grid-cols-2 gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          createMut.mutate(form);
        }}
      >
        <div>
          <label className="label" htmlFor="assessment-first_name">First name *</label>
          <input id="assessment-first_name" className="input" required value={form.first_name} onChange={set("first_name")} />
        </div>
        <div>
          <label className="label" htmlFor="assessment-last_name">Last name *</label>
          <input id="assessment-last_name" className="input" required value={form.last_name} onChange={set("last_name")} />
        </div>
        <div>
          <label className="label" htmlFor="assessment-email">Email *</label>
          <input id="assessment-email" className="input" type="email" required value={form.email ?? ""} onChange={set("email")} />
        </div>
        <div>
          <label className="label" htmlFor="assessment-contact_number">Contact number</label>
          <input id="assessment-contact_number" className="input" value={form.contact_number ?? ""} onChange={set("contact_number")} />
        </div>
        <div>
          <label className="label" htmlFor="assessment-home">Home</label>
          <select id="assessment-home" className="input" value={form.home ?? ""} onChange={set("home")}>
            <option value="">Select...</option>
            {(lookups?.home ?? []).map((h) => <option key={h.id} value={h.value}>{h.value}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="assessment-region">Region</label>
          <select id="assessment-region" className="input" value={form.region ?? ""} onChange={set("region")}>
            <option value="">Select...</option>
            {(lookups?.region ?? []).map((h) => <option key={h.id} value={h.value}>{h.value}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="assessment-position">Position applied for</label>
          <select id="assessment-position" className="input" value={form.position ?? ""} onChange={set("position")}>
            <option value="">Select...</option>
            {(lookups?.position ?? []).map((h) => <option key={h.id} value={h.value}>{h.value}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="assessment-assessment_date">Assessment date</label>
          <input id="assessment-assessment_date" className="input" type="date" value={form.assessment_date} onChange={set("assessment_date")} />
        </div>

        {retakeNeeded ? (
          <div className="md:col-span-2 bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800 space-y-2">
            <p className="font-semibold">Candidate already assessed</p>
            <p>{retakeNeeded}</p>
            {me?.sees_all && (
              <button
                type="button"
                className="btn-primary"
                disabled={createMut.isPending || !me?.test_window}
                onClick={() => createMut.mutate({ ...form, allow_retake: true })}
              >
                Approve retake and create
              </button>
            )}
          </div>
        ) : createMut.error ? (
          <p className="md:col-span-2 text-liaise-red text-sm">{String(createMut.error)}</p>
        ) : null}

        <div className="md:col-span-2 flex justify-end gap-3 pt-2">
          <Link to="/" className="btn-secondary">Cancel</Link>
          <button type="submit" className="btn-primary" disabled={createMut.isPending || !me?.test_window}>
            {createMut.isPending ? "Creating..." : "Create assessment"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CreatedScreen({
  created,
  me,
  candidateName,
  home,
  position,
  onDone,
}: {
  created: CreatedAssessment;
  me?: MeResponse;
  candidateName: string;
  home: string | null;
  position: string | null;
  onDone: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, created.candidate_url, { width: 220, margin: 1 }, () => {});
    }
  }, [created.candidate_url]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.candidate_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const print = () =>
    printCandidateSheet({
      candidateName: candidateName || "Candidate",
      testId: created.test_id,
      url: created.candidate_url,
      home,
      position,
      windowText: windowText(me?.test_window),
      linkText: `${created.expires_in_days} days${created.expires_at ? ` (until ${fmtDateTime(created.expires_at)})` : ""}`,
      timeLimitMinutes: me?.time_limit_minutes ?? 35,
    });

  return (
    <div className="max-w-xl mx-auto space-y-6 text-center">
      <div className="card p-8 space-y-6">
        <div className="w-14 h-14 mx-auto rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
          <span className="text-liaise-green text-2xl">✓</span>
        </div>
        <div>
          <h1 className="text-xl font-bold text-liaise-navy">Assessment ready</h1>
          <p className="text-sm text-slate-500 mt-1">
            Test ID <span className="font-mono font-bold text-liaise-navy">{created.test_id}</span> · link valid for {created.expires_in_days} days
          </p>
        </div>

        <canvas role="img" aria-label="Candidate assessment QR code" ref={canvasRef} className="mx-auto rounded-lg border border-slate-200 p-1" />

        <div className="space-y-2">
          <p className="text-xs text-slate-500 break-all px-4">{created.candidate_url}</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <button className="btn-secondary" onClick={copy}>{copied ? "Copied!" : "Copy link"}</button>
            <button className="btn-secondary" disabled={!me?.test_window} onClick={print}>Print sheet</button>
            <a className="btn-primary" href={created.candidate_url} target="_blank" rel="noopener noreferrer">
              Start on this device
            </a>
          </div>
          <p className="text-sm text-slate-600 pt-1">
            Hand this device to the candidate, let them scan the QR code, send them the link, or print a sheet to give them.
            {" "}{windowText(me?.test_window)}
          </p>
        </div>
      </div>
      <button className="text-sm text-liaise-blue hover:underline" onClick={onDone}>
        View assessment record →
      </button>
    </div>
  );
}
